import { SelectProps } from 'antd'
import { useMemo } from 'react'

import {
  useClassifierServiceGetCommonClassifierElementListEndpoint,
  useClassifierServiceGetInvestigationClassifierListEndpoint
} from 'src/api/queries'
import { ClassifierCategory, ClassifierType } from 'src/api/requests'
import { Select } from 'src/components'
import { useClassifierId } from 'src/hooks'
import { useLocales } from 'src/locales'

type Props = {
  categoryIds?: string[]
  isLoading?: boolean
} & SelectProps

export const InvestigationSpecimenTypesSelect = ({
  categoryIds,
  isLoading,
  ...rest
}: Props) => {
  const { t, currentLang } = useLocales()

  const specimenTypeClassifierId = useClassifierId(
    ClassifierType.SPECIMEN_TYPE,
    ClassifierCategory.INVESTIGATIONS
  )

  const { data: investigationData, isLoading: isInvestigationLoading } =
    useClassifierServiceGetInvestigationClassifierListEndpoint(
      {
        pageNumber: 1,
        pageSize: 100,
        ...(categoryIds && { categoryIds })
      },
      [categoryIds],
      { enabled: !!categoryIds }
    )

  const specimenTypeIds = investigationData?.data?.flatMap(
    (investigation) => investigation?.specimenTypeIds ?? []
  )

  const { data: specimenTypes, isLoading: isLoadingSpecimenTypes } =
    useClassifierServiceGetCommonClassifierElementListEndpoint(
      {
        id: String(specimenTypeClassifierId),
        pageNumber: 1,
        pageSize: 100
      },
      [specimenTypeClassifierId, specimenTypeIds],
      { enabled: !!specimenTypeClassifierId && !!specimenTypeIds }
    )

  const specimenTypeOptions = useMemo(
    () =>
      specimenTypes?.data
        ?.filter(
          (specimen) => specimen.id && specimenTypeIds?.includes(specimen.id)
        )
        .map((specimen) => ({
          value: specimen.id!,
          label: specimen.translation?.[currentLang.value] ?? ''
        })) ?? [],
    [specimenTypes?.data, currentLang.value, specimenTypeIds]
  )

  return (
    <Select
      placeholder={t('consultations.select_specimen_type')}
      options={specimenTypeOptions}
      loading={isLoading || isInvestigationLoading || isLoadingSpecimenTypes}
      allowClear
      {...rest}
    />
  )
}
